import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Table,
  Button,
  Tag,
  App,
  Space,
  Descriptions,
  Popconfirm,
  Collapse,
  Spin,
  Typography,
  Select,
  Modal,
  Form,
  Input,
  Alert,
  Checkbox,
} from 'antd';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  EditOutlined,
  CheckOutlined,
  StopOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { firstNonUuidDisplay } from '@/utils/displayLabels';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsReceiptNoteService,
  type GRNLineDTO,
  getGrnLineHeatLines,
  type GrnItemHeatLineDTO,
} from '@/services/goodsReceiptNote.service';
import { formatItemDescriptionWithProfile } from '@/components/inventory/ItemSelect';
import { chartOfAccountsService } from '@/services/salesInvoice.service';
import { supplierInvoiceService, type SupplierInvoiceDTO } from '@/services/supplierInvoice.service';
import { formatMoneyWithProfile, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { PrintableGRN } from '@/components/procurement/PrintableGRN';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import { useSystemSettings } from '@/hooks/useSettings';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { ENTITY_TYPE_GOODS_RECEIPT_NOTE } from '@/constants/approval-entity-types';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import assetService, { type GrnAssetLineSuggestion } from '@/services/asset.service';
import { useAuthStore } from '@/store/authStore';
import { headerProjectLabel, projectLabel } from '@/utils/projectDisplay';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import {
  GrnWeightVariance,
  expectedReceiptWeight,
  lineTheoreticalWeight,
} from '@/components/procurement/GrnWeightVariance';
import { formatKg, parseEngineeringSnapshot } from '@/services/engineeringMaterial.service';
import { DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE } from '@/constants/document-entity-types';

const { Text, Title } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

const GoodsReceiptNoteDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [creditAccountId, setCreditAccountId] = useState<string | undefined>(undefined);
  const [assetLineModal, setAssetLineModal] = useState<GrnAssetLineSuggestion | null>(null);
  const [assetForm] = Form.useForm<{ units: { serial_number?: string; asset_name?: string; remarks?: string }[] }>();
  const [allocModalOpen, setAllocModalOpen] = useState(false);
  const [allocSelections, setAllocSelections] = useState<Record<string, number[]>>({});
  const [allocContextLoading, setAllocContextLoading] = useState(false);
  const [allocContext, setAllocContext] = useState<
    Awaited<ReturnType<typeof goodsReceiptNoteService.getOutsourceAllocationContext>> | null
  >(null);

  const { data: settings } = useSystemSettings();
  const { resolve } = useUserDisplayResolver();
  const { isCriticalCode } = useCriticalItemLookup();
  const { data: grn, isLoading } = useQuery({
    queryKey: ['goods-receipt-note', id],
    queryFn: () => goodsReceiptNoteService.getById(id!),
    enabled: !!id,
  });
  const { data: departments = [] } = useDepartmentsForSelection();

  const canViewSupplierInvoices = Boolean(
    user?.is_super_admin || user?.permissions?.includes('procurement.invoice.view')
  );
  const { data: linkedInvoices = [], isLoading: linkedInvoicesLoading } = useQuery({
    queryKey: ['supplier-invoices-by-grn', id],
    queryFn: () => supplierInvoiceService.list({ grn_id: id! }),
    enabled: !!id && canViewSupplierInvoices,
  });

  const showFixedAssetGrnPanel = grn?.grn_status === 'Posted' && Boolean(id);
  const canCreateAssets = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.create'));

  const { data: grnAssetSug, isLoading: grnAssetSugLoading } = useQuery({
    queryKey: ['grn-asset-suggestions', id],
    queryFn: () => assetService.getGrnSuggestions(id!),
    enabled: showFixedAssetGrnPanel,
  });

  const canPost = Boolean(
    grn &&
      grn.grn_status !== 'Posted' &&
      grn.grn_status !== 'Cancelled' &&
      grn.grn_status !== 'Draft' &&
      (grn.grn_status === 'Approved' || !grn.approval_instance_id) &&
      (!grn.inspection_required || String(grn.inspection_status) === 'Passed')
  );

  const qcInspectionId = grn?.qc_inspection_record_id;

  const { data: postingDefaults, isLoading: postingDefaultsLoading } = useQuery({
    queryKey: ['grn-posting-defaults'],
    queryFn: () => goodsReceiptNoteService.getPostingDefaults(),
    enabled: !!id && canPost,
  });

  const { data: liabilityAccounts = [], isLoading: liabilityAccountsLoading } = useQuery({
    queryKey: ['chart-of-accounts', 'Liability', 'grn-credit'],
    queryFn: () => chartOfAccountsService.getAccountsByType('Liability', false),
    enabled: !!id && canPost,
  });

  useEffect(() => {
    if (canPost && postingDefaults?.default_credit_account_id) {
      setCreditAccountId(postingDefaults.default_credit_account_id);
    }
  }, [canPost, postingDefaults?.default_credit_account_id, id]);

  const creditAccountOptions = useMemo(() => {
    const list = liabilityAccounts.map((a) => ({
      value: a.id,
      label: `${a.account_code} — ${a.account_name}`,
    }));
    if (
      postingDefaults &&
      !list.some((o) => o.value === postingDefaults.default_credit_account_id)
    ) {
      list.unshift({
        value: postingDefaults.default_credit_account_id,
        label: `${postingDefaults.account_code} — ${postingDefaults.account_name} (system default)`,
      });
    }
    return list;
  }, [liabilityAccounts, postingDefaults]);

  const doPost = (allocations?: Array<{ grn_item_id: string; unit_numbers: number[] }>) =>
    goodsReceiptNoteService.post(id!, {
      credit_account_id: creditAccountId ?? postingDefaults?.default_credit_account_id,
      outsource_allocations: allocations,
    });

  const openPostWithAllocation = async () => {
    if (!id) return;
    setAllocContextLoading(true);
    try {
      const ctx = await goodsReceiptNoteService.getOutsourceAllocationContext(id);
      const needs = ctx.lines.filter((l) => l.requires_allocation);
      if (needs.length === 0) {
        await runAction('post', () => doPost());
        return;
      }
      const initial: Record<string, number[]> = {};
      for (const line of needs) {
        initial[line.grn_item_id] = [];
      }
      setAllocSelections(initial);
      setAllocContext(ctx);
      setAllocModalOpen(true);
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to load allocation context');
    } finally {
      setAllocContextLoading(false);
    }
  };

  const confirmAllocPost = async () => {
    if (!allocContext) return;
    const allocations = allocContext.lines
      .filter((l) => l.requires_allocation)
      .map((l) => ({
        grn_item_id: l.grn_item_id,
        unit_numbers: allocSelections[l.grn_item_id] ?? [],
      }));
    for (const line of allocContext.lines.filter((l) => l.requires_allocation)) {
      if ((allocSelections[line.grn_item_id] ?? []).length !== line.received_quantity) {
        message.error(
          `Line ${line.line_number}: select exactly ${line.received_quantity} unit(s)`
        );
        return;
      }
    }
    setAllocModalOpen(false);
    await runAction('post', () => doPost(allocations));
  };

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    setActionLoading(key);
    try {
      await fn();
      message.success('Done');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-note', id] });
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrint = () => {
    if (!grn) return;
    const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
    const companyName = companySettings.find((s: any) => s.setting_key === 'company_name')?.setting_value ?? 'Company';
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(<PrintableGRN grn={grn} companyName={companyName} onClose={cleanup} />);
    window.addEventListener('afterprint', cleanup);
  };

  const handlePrintVoucher = async () => {
    if (!id) return;
    try {
      const voucher = await goodsReceiptNoteService.getVoucherByGrnId(id);
      if (!voucher) {
        message.warning('No voucher found. Post this GRN first to generate the accounting entry.');
        return;
      }
      const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
      const companyName = companySettings.find((s: any) => s.setting_key === 'company_name')?.setting_value ?? '';
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableVoucher
          voucher={voucher}
          referenceLabel="GRN"
          companyName={companyName}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load voucher');
    }
  };

  const breadcrumbSegments = useMemo(
    () =>
      grn
        ? [
            { title: 'Supply Chain', path: '/scm/dashboard' },
            { title: 'Goods receipt', path: '/scm/procurement/grn' },
            { title: grn.grn_number || 'GRN' },
          ]
        : null,
    [grn?.id, grn?.grn_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const heatTraceabilityRows = useMemo(() => {
    if (!grn?.items?.length) return [];
    const rows: Array<
      GrnItemHeatLineDTO & { line_number: number; item_code?: string; item_description?: string }
    > = [];
    for (const line of grn.items) {
      for (const h of getGrnLineHeatLines(line)) {
        rows.push({
          ...h,
          line_number: line.line_number,
          item_code: line.item_code,
          item_description: line.item_description,
        });
      }
    }
    return rows;
  }, [grn?.items]);

  const hasCriticalMtcGapHint = useMemo(() => {
    if (!grn?.items?.length || grn.grn_status === 'Cancelled' || grn.grn_status === 'Draft') return false;
    for (const line of grn.items) {
      if (!isCriticalCode(line.item_code) || Number(line.accepted_quantity) <= 0) continue;
      const heats = getGrnLineHeatLines(line);
      if (!heats.length) return true;
      if (heats.some((h) => !String(h.mtc_document_id ?? '').trim())) return true;
    }
    return false;
  }, [grn?.items, grn?.grn_status, isCriticalCode]);

  const grnWorkflowStages = useMemo(() => {
    if (!grn) return [];
    const st = grn.grn_status;
    const cancelled = st === 'Cancelled';
    const posted = st === 'Posted';
    const inspRequired = Boolean(grn.inspection_required);
    const inspStatus = String(grn.inspection_status || 'Pending');
    const inspPassed = !inspRequired || inspStatus === 'Passed';
    const inspFailed = inspRequired && inspStatus === 'Failed';
    return [
      {
        title: 'GRN',
        description: st,
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      ...(inspRequired
        ? [
            {
              title: 'QMS Receiving QC',
              description: inspStatus,
              status: (inspFailed
                ? 'error'
                : inspPassed
                  ? 'finish'
                  : st === 'Approved'
                    ? 'process'
                    : 'wait') as 'wait' | 'process' | 'finish' | 'error',
            },
          ]
        : []),
      {
        title: 'Post / inventory',
        description: posted ? 'Posted' : inspFailed ? 'Blocked' : 'Pending',
        status: (cancelled || inspFailed
          ? 'wait'
          : posted
            ? 'finish'
            : inspPassed && (st === 'Approved' || st === 'Submitted')
              ? 'process'
              : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Purchase bill',
        description: 'Match to supplier invoice',
        status: (posted ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Payment',
        description: 'AP',
        status: 'wait' as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [grn]);

  const grnWorkflowCurrent = useMemo(() => {
    if (!grn) return 0;
    const st = grn.grn_status;
    const inspRequired = Boolean(grn.inspection_required);
    const inspPassed = !inspRequired || String(grn.inspection_status) === 'Passed';
    if (st === 'Draft') return 0;
    if (st === 'Cancelled') return 0;
    if (st === 'Posted') return inspRequired ? 3 : 2;
    if (st === 'Approved' && inspRequired && !inspPassed) return 1;
    if (st === 'Approved' || st === 'Submitted') return inspRequired ? 2 : 1;
    return 1;
  }, [grn]);

  if (!id) return null;
  if (isLoading && !grn) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!grn) return <Card><p>GRN not found.</p></Card>;

  const isDraft = grn.grn_status === 'Draft';
  const canEdit = isDraft;
  const canSubmit = isDraft;
  const showUniversalApproval = !!grn.approval_instance_id;
  const canApprove = grn.grn_status === 'Submitted' && !showUniversalApproval;
  const canUnpost = grn.grn_status === 'Posted';
  const canCancel = !['Posted', 'Cancelled'].includes(grn.grn_status);

  const primaryActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/grn')}>
        Back
      </Button>
      {canEdit && (
        <Button type="default" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/grn/${grn.id}/edit`)}>
          Edit
        </Button>
      )}
      <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
        Print
      </Button>
      <ReportDocumentActions
        reportKey="goods-receipt-note"
        params={{ id: grn.id }}
        documentId={grn.id}
        sendPermissions={['procurement.grn.edit', 'procurement.po.edit']}
        emailLabel="Email supplier"
      />
      {grn.gl_posted && (
        <Button type="default" icon={<FileTextOutlined />} onClick={handlePrintVoucher}>
          Print voucher
        </Button>
      )}
      {canSubmit && (
        <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => goodsReceiptNoteService.submit(id!))}>
          Submit
        </Button>
      )}
      {canApprove && (
        <Button
          type="primary"
          loading={actionLoading === 'approve'}
          icon={<CheckOutlined />}
          onClick={() => runAction('approve', () => goodsReceiptNoteService.approve(id!, undefined))}
        >
          Approve
        </Button>
      )}
      {grn.inspection_required && qcInspectionId && (
        <Button
          type="default"
          icon={<FileTextOutlined />}
          onClick={() => navigate(`/scm/procurement/receiving-inspections/${qcInspectionId}`)}
        >
          Open QC inspection
        </Button>
      )}
      {canPost && (
        <Button
          type="primary"
          loading={actionLoading === 'post' || allocContextLoading}
          disabled={!creditAccountId && (postingDefaultsLoading || liabilityAccountsLoading)}
          onClick={() => void openPostWithAllocation()}
        >
          Post
        </Button>
      )}
      {canUnpost && (
        <Popconfirm
          title="Unpost this GRN? Stock and PO received quantities will be reversed; the accounting voucher will be cancelled. Allowed only if no purchase invoice and no approved or posted purchase return exists for this GRN."
          onConfirm={() => runAction('unpost', () => goodsReceiptNoteService.unpost(id!))}
        >
          <Button loading={actionLoading === 'unpost'}>Unpost</Button>
        </Popconfirm>
      )}
      {canCancel && (
        <Popconfirm title="Cancel this GRN?" onConfirm={() => runAction('cancel', () => goodsReceiptNoteService.cancel(id!))}>
          <Button danger icon={<StopOutlined />} loading={actionLoading === 'cancel'}>
            Cancel
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>Related</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        {grn.po_id && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/scm/procurement/purchase-orders/${grn.po_id}`)}>
            Open purchase order
          </Button>
        )}
        {linkedInvoices.map((inv) => (
          <Button
            key={inv.id}
            type="link"
            block
            style={{ textAlign: 'left', paddingLeft: 0 }}
            onClick={() => navigate(`/scm/procurement/supplier-invoices/${inv.id}`)}
          >
            Supplier invoice {inv.invoice_number}
          </Button>
        ))}
        {canViewSupplierInvoices && linkedInvoices.length === 0 && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/scm/procurement/supplier-invoices')}>
            Supplier invoices
          </Button>
        )}
      </Space>
    </div>
  );

  const openGrnAssetModal = (line: GrnAssetLineSuggestion) => {
    setAssetLineModal(line);
    const max = line.remaining_units;
    const n = Math.min(1, Math.max(1, max));
    if (line.track_serial_number && line.suggested_serials.length > 0) {
      const pre = line.suggested_serials
        .slice(0, max)
        .map((serial_number) => ({ serial_number, asset_name: undefined as string | undefined, remarks: undefined as string | undefined }));
      assetForm.setFieldsValue({ units: pre.length ? pre : Array.from({ length: n }, () => ({})) });
    } else {
      assetForm.setFieldsValue({ units: Array.from({ length: n }, () => ({})) });
    }
  };

  const handleSubmitGrnAssets = async () => {
    if (!id || !assetLineModal) return;
    try {
      const v = await assetForm.validateFields();
      if (!v.units?.length) {
        message.error('Add at least one row');
        return;
      }
      if (v.units.length > assetLineModal.remaining_units) {
        message.error(`Only ${assetLineModal.remaining_units} unit(s) can still be created for this line.`);
        return;
      }
      setActionLoading('assets');
      await assetService.createFromGrn({
        grn_id: id,
        grn_item_id: assetLineModal.grn_item_id,
        units: v.units,
      });
      message.success('Assets created');
      setAssetLineModal(null);
      await queryClient.invalidateQueries({ queryKey: ['grn-asset-suggestions', id] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Failed to create assets');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div style={{ margin: '-20px -20px 0', marginBottom: 0 }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Goods receipt note"
            documentNumber={grn.grn_number}
            statusTag={
              <Space wrap>
                <Tag color={statusColors[grn.grn_status] ?? 'default'}>{grn.grn_status}</Tag>
                <Tag>{grn.receipt_type}</Tag>
                {grn.is_posted && <Tag color="green">Posted</Tag>}
                {grn.inspection_required && (
                  <Tag
                    color={
                      grn.inspection_status === 'Passed'
                        ? 'success'
                        : grn.inspection_status === 'Failed'
                          ? 'error'
                          : 'warning'
                    }
                  >
                    QC: {grn.inspection_status || 'Pending'}
                  </Tag>
                )}
              </Space>
            }
            subtitle={
              <Text type="secondary">{grn.grn_date ? dayjs(grn.grn_date).format('DD MMM YYYY') : ''}</Text>
            }
            facts={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>
                  <strong>Supplier:</strong> {grn.supplier?.supplier_name ?? grn.supplier_id ?? '—'}
                </Text>
                {canPost && (
                  <div style={{ maxWidth: 520 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Credit account when posting (provision for AP / trade payable)
                    </Text>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select ledger account to credit"
                      style={{ width: '100%' }}
                      loading={postingDefaultsLoading || liabilityAccountsLoading}
                      value={creditAccountId}
                      onChange={(v) => setCreditAccountId(v)}
                      options={creditAccountOptions}
                    />
                  </div>
                )}
                {grn.grn_status === 'Posted' &&
                  (grn.grnCreditAccount || grn.grn_credit_account_id) && (
                    <Text type="secondary">
                      <strong>Posted credit account:</strong>{' '}
                      {grn.grnCreditAccount
                        ? `${grn.grnCreditAccount.account_code} — ${grn.grnCreditAccount.account_name}`
                        : grn.grn_credit_account_id}
                    </Text>
                  )}
              </Space>
            }
            primaryActions={primaryActions}
          />
        }
        workflow={<WorkflowStrip stages={grnWorkflowStages} current={grnWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <TaskRoutingCard
          sourceDocumentType="GoodsReceiptNote"
          sourceDocumentId={grn.id}
          nextActionUserId={(grn as { next_action_user_id?: string | null }).next_action_user_id}
          documentOwnerLabel="Received by"
          documentOwnerUserId={grn.received_by}
          disabled={grn.grn_status === 'Cancelled' || grn.grn_status === 'Posted'}
          invalidateQueryKeys={[['goods-receipt-note', id!]]}
        />
        {grn.inspection_required && grn.grn_status === 'Approved' && String(grn.inspection_status) !== 'Passed' && (
          <Alert
            style={{ marginBottom: 16 }}
            showIcon
            type={grn.inspection_status === 'Failed' ? 'error' : 'warning'}
            message={
              grn.inspection_status === 'Failed'
                ? 'QC failed in Quality Receiving Inspection — posting and supplier invoice are blocked.'
                : 'Receiving QC must Pass in Quality before posting. QA completes this under Receiving Inspections (optional Dimension Inspection may apply).'
            }
            description={
              <Space direction="vertical" size={4}>
                {grn.inspection_notes ? <span>{grn.inspection_notes}</span> : null}
                {qcInspectionId ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/scm/procurement/receiving-inspections/${qcInspectionId}`)}
                  >
                    Open linked Receiving Inspection
                  </Button>
                ) : (
                  <Text type="secondary">
                    Inspection will appear after Approve (or run backfill for legacy GRNs).
                  </Text>
                )}
              </Space>
            }
          />
        )}
        {showUniversalApproval && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_GOODS_RECEIPT_NOTE}
              entityId={grn.id}
              onApproveViaDocument={async (remarks) => {
                await goodsReceiptNoteService.approve(id!, remarks);
              }}
              onRejectViaDocument={async (payload) => {
                await goodsReceiptNoteService.rejectApproval(id!, payload);
              }}
              extraInvalidateQueryKeys={[['goods-receipt-note', id], ['goods-receipt-notes']]}
            />
          </div>
        )}
        <Collapse
          defaultActiveKey={['header', 'lines']}
          items={[
            {
              key: 'header',
              label: 'Receipt details',
              children: (
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                  <Descriptions.Item label="GRN date">{grn.grn_date ? dayjs(grn.grn_date).format('DD MMM YYYY') : '—'}</Descriptions.Item>
                  <Descriptions.Item label="PO number">
                    <DocumentRefLink
                      to={grn.po_id ? `/scm/procurement/purchase-orders/${grn.po_id}` : undefined}
                      number={grn.purchaseOrder?.po_number}
                      id={grn.po_id}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Supplier">
                    {firstNonUuidDisplay(grn.supplier?.supplier_name)}
                  </Descriptions.Item>
                  {(grn.inspection_required || qcInspectionId) && (
                    <Descriptions.Item label="Receiving inspection">
                      {qcInspectionId ? (
                        <DocumentRefLink
                          to={`/scm/procurement/receiving-inspections/${qcInspectionId}`}
                          number={grn.qc_inspection_number}
                          id={qcInspectionId}
                        />
                      ) : (
                        '—'
                      )}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Project">{headerProjectLabel(grn, grn.items)}</Descriptions.Item>
                  <Descriptions.Item label="Department">
                    {grn.department_id
                      ? (() => {
                          const d = departments.find((x) => x.id === grn.department_id);
                          return d ? `${d.department_code} - ${d.department_name}` : grn.department_id;
                        })()
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Warehouse">{grn.warehouse?.warehouse_name ?? grn.warehouse_location ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Received by">
                    {grn.received_by_display?.trim() || resolve(grn.received_by, '—')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Supplier delivery note">{grn.supplier_delivery_note || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Vehicle number">{grn.vehicle_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Posted date">
                    {grn.posted_date ? dayjs(grn.posted_date).format('DD MMM YYYY HH:mm') : '—'}
                  </Descriptions.Item>
                  {(grn.grnCreditAccount || grn.grn_credit_account_id) && (
                    <Descriptions.Item label="GRN credit (GL)" span={2}>
                      {grn.grnCreditAccount
                        ? `${grn.grnCreditAccount.account_code} — ${grn.grnCreditAccount.account_name}`
                        : grn.grn_credit_account_id}
                    </Descriptions.Item>
                  )}
                  {grn.notes && <Descriptions.Item label="Notes" span={3}>{grn.notes}</Descriptions.Item>}
                </Descriptions>
              ),
            },
            {
              key: 'lines',
              label: `Line items (${(grn.items ?? []).length})`,
              children: (
                <div>
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={grn.items ?? []}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    rowClassName={(r: GRNLineDTO) =>
                      getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
                    }
                    expandable={{
                      rowExpandable: (r) => getGrnLineHeatLines(r).length > 0,
                      expandedRowRender: (r) => (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(h) => h.id}
                          dataSource={getGrnLineHeatLines(r)}
                          columns={[
                            { title: 'Heat number', dataIndex: 'heat_number', width: 140 },
                            { title: 'Dimension', dataIndex: 'dimension', width: 140, render: (v: string) => v || '—' },
                            {
                              title: 'Qty',
                              dataIndex: 'quantity',
                              width: 100,
                              align: 'right',
                              render: (v: number) => Number(v).toLocaleString(),
                            },
                            {
                              title: 'MTC document',
                              key: 'mtc_doc',
                              width: 300,
                              render: (_: unknown, h: GrnItemHeatLineDTO) => (
                                <MtcDocumentLink
                                  documentId={h.mtc_document_id}
                                  documentNumber={h.mtc_document_number}
                                  compact
                                />
                              ),
                            },
                          ]}
                        />
                      ),
                    }}
                    columns={[
                      { title: '#', dataIndex: 'line_number', width: 50 },
                      {
                        title: 'Heat / MTC',
                        key: 'heat_mtc',
                        width: 120,
                        fixed: 'left' as const,
                        render: (_: unknown, row: GRNLineDTO) => {
                          const n = getGrnLineHeatLines(row).length;
                          return n > 0 ? (
                            <Tag color="blue">
                              {n} heat line{n !== 1 ? 's' : ''} — expand
                            </Tag>
                          ) : (
                            <Text type="secondary">—</Text>
                          );
                        },
                      },
                      {
                        title: 'Item',
                        dataIndex: 'item_code',
                        width: 140,
                        render: (code: string, r: GRNLineDTO) => (
                          <ItemCodeWithCritical code={code} critical={isCriticalCode(r.item_code)} />
                        ),
                      },
                      {
                        title: 'Description',
                        dataIndex: 'item_description',
                        width: 220,
                        render: (desc: string, row: GRNLineDTO) => (
                          <>
                            <div>{formatItemDescriptionWithProfile(desc, row) || desc || '—'}</div>
                            <LineSpecificationsText
                              value={row.specifications ?? row.purchaseOrderItem?.specifications}
                            />
                            {row.purchaseOrderItem?.stock_quantity != null ? (
                              <div style={{ fontSize: 12, color: '#888' }}>
                                Stock in: {Number(row.purchaseOrderItem.stock_quantity)}{' '}
                                {row.purchaseOrderItem.stock_uom || ''}
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                      {
                        title: 'Supplier Description',
                        dataIndex: 'supplier_description',
                        width: 200,
                        render: (v: string, row: GRNLineDTO) => v || row.item_description || '—',
                      },
                      { title: 'Project', key: 'project', width: 120, render: (_: unknown, row: GRNLineDTO) => projectLabel(row) },
                      { title: 'Ordered', dataIndex: 'ordered_quantity', width: 80, align: 'right', render: (v: number) => Number(v).toLocaleString() },
                      { title: 'Received', dataIndex: 'received_quantity', width: 90, align: 'right', render: (v: number) => Number(v).toLocaleString() },
                      { title: 'Accepted', dataIndex: 'accepted_quantity', width: 90, align: 'right', render: (v: number) => Number(v).toLocaleString() },
                      { title: 'Rejected', dataIndex: 'rejected_quantity', width: 80, align: 'right', render: (v: number) => Number(v ?? 0).toLocaleString() },
                      { title: 'Hold', dataIndex: 'hold_quantity', width: 70, align: 'right', render: (v: number) => Number(v ?? 0).toLocaleString() },
                      { title: 'UOM', dataIndex: 'uom', width: 55 },
                      ...((grn.items ?? []).some((l) => l.actual_weight != null || lineTheoreticalWeight(l) != null)
                        ? [
                            {
                              title: 'Actual weight',
                              key: 'actual_weight',
                              width: 160,
                              render: (_: unknown, row: GRNLineDTO) => {
                                const def = parseEngineeringSnapshot(row.purchaseOrderItem?.engineering_snapshot)?.definition;
                                const actual = row.actual_weight != null ? Number(row.actual_weight) : null;
                                return (
                                  <div>
                                    <div>{actual != null ? formatKg(actual) : '—'}</div>
                                    <GrnWeightVariance
                                      expected={expectedReceiptWeight(
                                        lineTheoreticalWeight(row),
                                        row.received_quantity,
                                        row.purchaseOrderItem?.ordered_quantity ?? row.ordered_quantity
                                      )}
                                      actual={actual}
                                      tolerancePlusPct={def?.tolerance_plus_pct}
                                      toleranceMinusPct={def?.tolerance_minus_pct}
                                    />
                                  </div>
                                );
                              },
                            },
                          ]
                        : []),
                      { title: 'Notes', dataIndex: 'notes', ellipsis: true, width: 120 },
                    ]}
                  />
                  <div style={{ marginTop: 20 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Heat lines &amp; MTC (traceability)
                    </Text>
                    {hasCriticalMtcGapHint && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={
                          <span>
                            Critical item heat/MTC may be incomplete. Remediate on the{' '}
                            <Link to="/scm/quality/critical-mtc">Critical MTC gaps</Link> page without
                            reopening this GRN.
                          </span>
                        }
                      />
                    )}
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Full list for this GRN (same data as expanded rows). Use expand on the table above for
                      per-line detail.
                    </Text>
                    {heatTraceabilityRows.length > 0 ? (
                      <Table
                        size="small"
                        rowKey={(r) => `${r.line_number}-${r.id}`}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        dataSource={heatTraceabilityRows}
                        rowClassName={(r) =>
                          getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
                        }
                        columns={[
                          { title: 'Line #', dataIndex: 'line_number', width: 70 },
                          {
                            title: 'Item',
                            dataIndex: 'item_code',
                            width: 140,
                            render: (code: string, r) => (
                              <ItemCodeWithCritical code={code} critical={isCriticalCode(r.item_code)} />
                            ),
                          },
                          {
                            title: 'Description',
                            dataIndex: 'item_description',
                            ellipsis: true,
                            width: 200,
                            render: (desc: string) => desc || '—',
                          },
                          { title: 'Heat number', dataIndex: 'heat_number', width: 120 },
                          {
                            title: 'Dimension',
                            dataIndex: 'dimension',
                            width: 120,
                            render: (v: string) => v || '—',
                          },
                          {
                            title: 'Qty',
                            dataIndex: 'quantity',
                            width: 90,
                            align: 'right',
                            render: (v: number) => Number(v).toLocaleString(),
                          },
                          {
                            title: 'MTC document',
                            key: 'mtc_doc',
                            width: 300,
                            render: (
                              _: unknown,
                              r: GrnItemHeatLineDTO & {
                                line_number: number;
                                item_code?: string;
                                item_description?: string;
                              }
                            ) => (
                              <MtcDocumentLink
                                documentId={r.mtc_document_id}
                                documentNumber={r.mtc_document_number}
                                compact
                              />
                            ),
                          },
                        ]}
                      />
                    ) : (
                      <Text type="secondary">
                        No heat lines are stored on this GRN. If the item requires heat/MTC, enter them on the
                        draft GRN before post, or use Edit (while Draft) to add them.
                      </Text>
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
        {canViewSupplierInvoices && (
          <Card size="small" title="Supplier invoices" style={{ marginTop: 16 }} data-testid="grn-supplier-invoices-panel">
            {linkedInvoices.length === 0 && !linkedInvoicesLoading ? (
              <Text type="secondary">
                {grn.grn_status === 'Posted'
                  ? 'No supplier invoice has been booked against this GRN yet.'
                  : 'Supplier invoices can be booked after this GRN is posted.'}
              </Text>
            ) : (
              <Table<SupplierInvoiceDTO>
                size="small"
                rowKey="id"
                pagination={false}
                loading={linkedInvoicesLoading}
                dataSource={linkedInvoices}
                columns={[
                  {
                    title: 'Invoice #',
                    dataIndex: 'invoice_number',
                    render: (v: string, r) => (
                      <Link to={`/scm/procurement/supplier-invoices/${r.id}`}>{v}</Link>
                    ),
                  },
                  { title: 'Supplier invoice #', dataIndex: 'supplier_invoice_number', render: (v) => v || '—' },
                  {
                    title: 'Invoice date',
                    dataIndex: 'supplier_invoice_date',
                    render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'invoice_status',
                    render: (v: string, r) => (
                      <Space size={4} wrap>
                        <Tag>{v}</Tag>
                        {r.gl_posted && <Tag color="green">Posted to GL</Tag>}
                      </Space>
                    ),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'grand_total',
                    align: 'right' as const,
                    render: (v, r) =>
                      v != null
                        ? `${r.currency ?? ''} ${formatMoneyWithProfile(v, PRINT_PLAIN_AMOUNT_PROFILE)}`.trim()
                        : '—',
                  },
                  {
                    title: 'Balance due',
                    dataIndex: 'balance_due',
                    align: 'right' as const,
                    render: (v) => (v != null ? formatMoneyWithProfile(v, PRINT_PLAIN_AMOUNT_PROFILE) : '—'),
                  },
                ]}
              />
            )}
          </Card>
        )}
        {showFixedAssetGrnPanel && (
          <Card
            size="small"
            title="Fixed assets (from this GRN)"
            style={{ marginTop: 16 }}
            data-testid="grn-fixed-assets-panel"
          >
            {grnAssetSugLoading && <Spin />}
            {grnAssetSug && (
              <>
                {grnAssetSug.block_reason && (
                  <Alert type="info" showIcon style={{ marginBottom: 12 }} message={grnAssetSug.block_reason} />
                )}
                {grnAssetSug.lines.length > 0 && (
                  <Table<GrnAssetLineSuggestion>
                    size="small"
                    rowKey="grn_item_id"
                    dataSource={grnAssetSug.lines}
                    pagination={false}
                    rowClassName={(r) =>
                      getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
                    }
                    columns={[
                      { title: 'Line', dataIndex: 'line_number', width: 64 },
                      {
                        title: 'Item',
                        key: 'it',
                        render: (r) => (
                          <Space size={6} wrap>
                            <span>
                              {r.item_code} — {r.item_name}
                            </span>
                            <CriticalItemTag critical={isCriticalCode(r.item_code)} />
                          </Space>
                        ),
                      },
                      { title: 'Remaining units', dataIndex: 'remaining_units', width: 120, align: 'right' as const },
                      { title: 'Unit value', dataIndex: 'unit_purchase_value', width: 110, align: 'right' as const, render: (v) => (v != null ? Number(v).toLocaleString() : '—') },
                      { title: 'Serial tracking', dataIndex: 'track_serial_number', width: 110, render: (v: boolean) => (v ? 'Required' : 'Optional') },
                      {
                        title: 'Action',
                        key: 'a',
                        width: 130,
                        render: (_, row) => (
                          <Button type="link" size="small" disabled={!canCreateAssets} onClick={() => openGrnAssetModal(row)}>
                            Create assets
                          </Button>
                        ),
                      },
                    ]}
                  />
                )}
                {!grnAssetSug.lines.length && !grnAssetSugLoading && canCreateAssets && (
                  <Text type="secondary">
                    No fixed-asset lines with remaining units. On the item master, enable &quot;Fixed asset — create assets from
                    goods receipt (GRN)&quot;, then receive and post this GRN.
                  </Text>
                )}
              </>
            )}
          </Card>
        )}
        <Modal
          title="Create assets from GRN"
          open={!!assetLineModal}
          onCancel={() => {
            setAssetLineModal(null);
            assetForm.resetFields();
          }}
          okText="Create"
          onOk={handleSubmitGrnAssets}
          okButtonProps={{ disabled: !canCreateAssets, loading: actionLoading === 'assets' }}
        >
          {assetLineModal && (
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Line {assetLineModal.line_number} — {assetLineModal.item_name} — up to {assetLineModal.remaining_units}{' '}
                unit(s). New assets are created in <Text strong>draft</Text> status; capitalization can follow your approval
                process.
              </Text>
              <Form form={assetForm} layout="vertical">
                <Form.List name="units">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => (
                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start" wrap>
                          <Form.Item
                            name={[field.name, 'serial_number']}
                            label="Serial"
                            rules={
                              assetLineModal.track_serial_number
                                ? [{ required: true, message: 'Serial is required' }]
                                : []
                            }
                            style={{ minWidth: 160, marginBottom: 0 }}
                          >
                            <Input placeholder="Serial" />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, 'asset_name']}
                            label="Asset name (optional)"
                            style={{ minWidth: 200, marginBottom: 0 }}
                          >
                            <Input placeholder="Defaults if empty" />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, 'remarks']}
                            label="Remarks"
                            style={{ minWidth: 120, marginBottom: 0 }}
                          >
                            <Input placeholder="Optional" />
                          </Form.Item>
                          <Button type="text" onClick={() => remove(field.name)} danger>
                            Remove
                          </Button>
                        </Space>
                      ))}
                      {fields.length < (assetLineModal?.remaining_units ?? 0) && (
                        <Button
                          type="dashed"
                          onClick={() => {
                            if (assetLineModal && fields.length >= assetLineModal.remaining_units) return;
                            add();
                          }}
                          block
                        >
                          Add another unit
                        </Button>
                      )}
                    </>
                  )}
                </Form.List>
              </Form>
            </div>
          )}
        </Modal>

        <Modal
          title="Allocate to production units"
          open={allocModalOpen}
          onCancel={() => setAllocModalOpen(false)}
          onOk={() => void confirmAllocPost()}
          okText="Post GRN"
          width={720}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Fabrication outsourced lines require one production unit per received quantity before posting."
          />
          {allocContext?.lines
            .filter((l) => l.requires_allocation)
            .map((line) => (
              <div key={line.grn_item_id} style={{ marginBottom: 16 }}>
                <Text strong>
                  Line {line.line_number}: {line.item_description}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Received {line.received_quantity} — select {line.received_quantity} unit(s)
                </Text>
                <Checkbox.Group
                  value={allocSelections[line.grn_item_id] ?? []}
                  onChange={(vals) =>
                    setAllocSelections((prev) => ({
                      ...prev,
                      [line.grn_item_id]: vals as number[],
                    }))
                  }
                  options={line.eligible_units
                    .filter((u) => !u.already_allocated)
                    .map((u) => ({
                      label: `Unit ${u.production_unit_number}`,
                      value: u.production_unit_number,
                      disabled: u.already_allocated,
                    }))}
                />
              </div>
            ))}
        </Modal>
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE}
          entityId={id}
          readOnly={grn.grn_status === 'Cancelled' || grn.grn_status === 'Posted'}
          queryKeyPrefix={['documents', 'goods-receipt-note']}
        />
      </WorkspaceLayout>
    </div>
  );
};

export default GoodsReceiptNoteDetailPage;
